import { RefreshCw } from 'lucide-react'

import { Button, ReceiptsFilters, ReceiptsTable, StatusMessage, Workbench, type ReceiptsFiltersProps } from '@krp/ui-kit'

import { OneCQueryState } from '../../../shared/ui/OneCQueryState'
import '../../../shared/ui/onec.css'
import { CASH_RECEIPTS_MAX_DAYS } from '../model/selectors'
import type { CashKindFilter, CashReceiptsScreenActions, CashReceiptsScreenVm } from '../model/types'
import { CashReceiptsPlacePicker } from './CashReceiptsPlacePicker'
import './receipts.css'

/**
 * То, чего нет во вкладке «Чеки» и что приносит только «Статистика по продажам»: итоги, график, прогноз и сообщения об их чтении.
 * Тип — срез свойств общих фильтров; этот файл сводок не знает и не импортирует.
 */
export type CashReceiptsExtras = Pick<ReceiptsFiltersProps, 'stats' | 'statsBusy' | 'notices' | 'trend' | 'periodTrend' | 'trendBusy' | 'todayBusy' | 'trendMessage'>

/**
 * «Чеки 1С»: чеки ККМ и возвраты базы 1С, как их ведёт 1С. Слева — дерево «Где смотрим» (организация → магазин или склад
 * → касса), справа — дни текущей недели, крупная сводка (приход, возвраты, итог), отбор по виду и поиск, а ниже сами чеки:
 * новые сверху, старые снизу. Только чтение: свои продажи пробивает касса своего источника. Вид документа — словом и
 * значком, а не цветом. 1С не ответила — её текст и «Повторить», а не пустой список.
 */
export function CashReceiptsScreen({ vm, actions, extras = {}, receipts = true }: {
  vm: CashReceiptsScreenVm
  actions: CashReceiptsScreenActions
  extras?: CashReceiptsExtras
  /** Список чеков и поиск по номеру; у «Статистики по продажам» их нет — там только итоги и графики, чеки не читаются. */
  receipts?: boolean
}) {
  const nav = vm.nav
  const { notices: extraNotices, ...summaries } = extras
  return (
    <section className="onec-screen" aria-label="Журнал чеков базы">
      {nav.note !== null && <p className="onec-field__note">{nav.note}</p>}
      <Workbench
        sideTitle="Где смотрим"
        sideSummary={nav.summaryText}
        side={
          <>
            <Button variant="secondary" data-size="sm" icon={<RefreshCw size={16} aria-hidden="true" />} busy={vm.refreshing} onClick={actions.refresh}>
              Обновить всю базу
            </Button>
            {nav.query.status === 'ready' ? (
              <CashReceiptsPlacePicker nav={nav} onSelect={actions.nodeChosen} onToggle={actions.nodeToggled} />
            ) : (
              <OneCQueryState query={nav.query} emptyText="В базе нет касс ККМ" onRetry={actions.retry} />
            )}
          </>
        }
      >
        <div className="onec-toolbar krp-sticky-refresh">
          <Button variant="secondary" icon={<RefreshCw size={16} aria-hidden="true" />} busy={vm.refreshing} onClick={actions.refresh}>
            Обновить
          </Button>
          {vm.readAt !== null && <span className="onec-muted">{vm.readAt}</span>}
        </div>
        <ReceiptsFilters
          today={vm.today}
          period={vm.period}
          onPeriod={actions.periodChanged}
          maxDays={CASH_RECEIPTS_MAX_DAYS}
          allTimeFrom={vm.allTimeFrom}
          dataFrom={vm.firstDay}
          {...summaries}
          notices={
            <>
              {extraNotices}
              {vm.truncatedNote !== null && <StatusMessage kind="empty" title="Показаны не все чеки" text={vm.truncatedNote} />}
            </>
          }
          kinds={vm.kinds}
          kind={vm.kind}
          onKind={(kind) => actions.kindChanged(kind as CashKindFilter)}
          cashiers={vm.cashiers}
          cashier={vm.cashier}
          onCashier={actions.cashierChanged}
          cashierHint="В чеках этой базы кассир не указан или его нет в справочнике пользователей: выбирать некого."
          searchShown={receipts && vm.mode !== 'range'}
          search={vm.search}
          searchLabel="Поиск по номеру, кассе, сумме"
          onSearch={actions.searchChanged}
          days={vm.days}
          onDay={(day) => actions.periodChanged({ from: day, to: day })}
        />
        {receipts && vm.mode === 'range' && vm.query.status === 'ready' && vm.periodError === null && (
          <p className="onec-field__note">
            За период из нескольких дней показана только сводка — чеки не выгружаются. Чтобы увидеть чеки, выберите один день: на графике.
          </p>
        )}
        {vm.periodError !== null ? (
          <StatusMessage kind="empty" title={vm.periodError} />
        ) : (
          receipts && <OneCQueryState query={vm.query} emptyText="За период чеков нет" onRetry={actions.retry} />
        )}
        {receipts && vm.filteredOut && <StatusMessage kind="empty" title="По этому отбору чеков нет" text="Число справа от названия в дереве — сколько чеков у места за период. Выберите место, где оно не ноль, или смените вид чека, поиск, период." />}
        {receipts && vm.rows.length > 0 && (
          <ReceiptsTable label="Чеки базы" rows={vm.rows} onOpen={actions.openReceipt} columns={[
            { id: 'shift', label: 'Смена', cell: (row) => row.shift, title: (row) => row.shift },
            { id: 'status', label: 'Состояние', cell: (row) => row.status, title: (row) => row.status },
          ]} />
        )}
      </Workbench>
    </section>
  )
}
